
// React imports
import { useState, useEffect, useMemo } from 'react';

// General imports
import _ from 'lodash';
import axios from 'axios';

// MUI imports
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import IconButton from '@mui/material/IconButton';
import Tooltip from '@mui/material/Tooltip';
import CircularProgress from '@mui/material/CircularProgress';

// FontAwesome imports
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCopy, faCheck, faFilePdf, faDownload } from '@fortawesome/pro-light-svg-icons';

// Local imports
import OpportunityPreviewAndSendBase from './OpportunityPreviewAndSendBase.js';
import { offerToText } from './OpportunityOneOffEmailBuilder.utils.js';
import { quotePdfFilename } from '../utils/downloadQuotePdf.js';
import { useQuotePdfDownload, QuotePdfNotice } from '../components/QuotePdfDownload.js';
import getBaseUrl from '../../GetBaseUrl.js';

function OpportunityPreviewAndSendOneOffEmail(props) {
    const { opportunity, accesstoken, organization, exchangeRates, onUpdateOpportunity, lockedExchangeRates, attachPDF } = props;
    const [ link, setLink ] = useState(null);
    const [ previewUrl, setPreviewUrl ] = useState(null);
    // Downloads the attachment itself. This used to open the offer page with type=preview,
    // which is the copy stamped PREVIEW rather than the file the customer receives.
    const { downloadingPdf, pdfError, downloadPdf: onDownloadPdf, notice: pdfNotice, dismissNotice: dismissPdfNotice } =
        useQuotePdfDownload({ opportunity, accesstoken });
    const [ expanded, setExpanded ] = useState(opportunity.type === 'one-off-free-text');
    const [ copied, setCopied ] = useState(null);

    useEffect(() => {
        if (opportunity.status === 'deleted') return;
        if (opportunity.type === 'one-off-free-text' && !expanded) {
            setExpanded(true);
            return;
        }
        (async () => {
            try {
                const { data } = await axios(`/api/opportunities/${opportunity.id}/offer-url?accesstoken=${accesstoken}&type=end-customer&organizationId=${organization.id}&minimized=${expanded ? 'false' : 'true'}`)
                setLink(`${getBaseUrl()}/offers?temporaryAccesstoken=${data}`);
                const { data: preview } = await axios(`/api/opportunities/${opportunity.id}/offer-url?accesstoken=${accesstoken}&type=preview&organizationId=${organization.id}&minimized=${expanded ? 'false' : 'true'}`)
                setPreviewUrl(preview);
            } catch (error) {
                console.error(error);
            }
        })();
    }, [ opportunity, accesstoken, expanded ]);

    const [email, emailPreview] = useMemo(() => {

        const terms = opportunity.terms;
        const transportationMethod = opportunity.transportationMethod;
        // If terms is explicitly set (even if empty array), use it. Otherwise use default from org.
        const _terms = (terms !== null && terms !== undefined) ? terms : (_.get(organization, `terms['price-quote-${transportationMethod}']`, null) || _.get(organization, 'terms[\'price-quote\']', null));
        const __terms = !!_terms && _.isString(_terms) ? [{ value: _terms }] : _terms;

        let body = offerToText({ 
            type: opportunity.type,
            offers: opportunity.offers, 
            preface: opportunity.preface, 
            epilogue: (opportunity.epilogue ? `${opportunity.epilogue}` : ''), 
            products: opportunity.products, 
            transportationMethod: transportationMethod, 
            buyingRates: opportunity.buyingRates,
            exchangeRates,
            expanded,
            link,
            terms: __terms,
            lockedExchangeRates,
            organization
        });
        const linkToken = link?.split('temporaryAccesstoken=')[1] || link;
        const previewToken = previewUrl?.split('temporaryAccesstoken=')[1] || previewUrl;
        const email = body.replace('PLACEHOLDER_FOR_LINK', `app.monada.ai/offers?temporaryAccesstoken=${linkToken}`);
        const emailPreview = body.replace('PLACEHOLDER_FOR_LINK', `app.monada.ai/offers?temporaryAccesstoken=${previewToken}`);
        return [email, emailPreview];
    }, [ opportunity, exchangeRates, expanded, link, previewUrl, organization ]);


    async function copyToClip() {
        const transportationMethod = opportunity.transportationMethod;
        const terms = opportunity.terms;
        const _terms = !_.isEmpty(terms) ? terms : _.get(organization, `terms['price-quote-${transportationMethod}']`, null) || _.get(organization, 'terms[\'price-quote\']', null);
        const __terms = !!_terms && _.isString(_terms) ? [{ value: _terms }] : _terms;
        (async () => {
            const { data: postedOpportunity } = await axios.put(`/api/opportunities/${opportunity.id}?organizationId=${organization.id}&accesstoken=${accesstoken}`, {
                opportunity: {
                    status: 'sent-to-endcustomer',
                }
            });
            onUpdateOpportunity({ opportunity: { ...opportunity, ...postedOpportunity } });
        })();
        let str = offerToText({
            type: opportunity.type,
            offers: opportunity.offers,
            preface: opportunity.preface,
            epilogue: (opportunity.epilogue ? `${opportunity.epilogue}` : ''), 
            products: opportunity.products,
            transportationMethod: transportationMethod,
            buyingRates: opportunity.buyingRates,
            exchangeRates,
            expanded,
            link,
            terms: __terms,
            lockedExchangeRates,
            organization
        });
        const clipboardToken = link?.split('temporaryAccesstoken=')[1] || link;
        str = str.replace('PLACEHOLDER_FOR_LINK', `app.monada.ai/offers?temporaryAccesstoken=${clipboardToken}`);
        function listener(e) {
            e.clipboardData.setData('text/html', str);
            e.clipboardData.setData('text/plain', str);
            e.preventDefault();
        }
        document.addEventListener('copy', listener);
        document.execCommand('copy');
        document.removeEventListener('copy', listener);
        setCopied('copied');
        setTimeout(() => setCopied(null), 1000);
    }

    if (!link) {
        return (
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%' }}>
                <CircularProgress />
            </Box>
        )
    }

    return (
        <OpportunityPreviewAndSendBase {...props} type='one-off-email' emailBody={email} minimized={!expanded}>
            {opportunity.status !== 'deleted' && (
                <Box sx={{ 
                    margin: '0px 16px', 
                    width: 'calc(100% - 32px)', 
                    padding: '24px',
                    backgroundColor: '#f8fafc',
                    display: 'flex',
                    justifyContent: 'center',
                    minHeight: '400px'
                }}>
                    <Box sx={{
                        maxWidth: '680px',
                        width: '100%',
                        backgroundColor: '#ffffff',
                        borderRadius: '8px',
                        boxShadow: '0 4px 6px -1px rgb(0 0 0 / 0.1)',
                        padding: '32px',
                        position: 'relative'
                    }}>
                        <Box
                            onClick={(e) => {
                                const link = e.target.closest('a');
                                if (link) {
                                    e.preventDefault();
                                    e.stopPropagation();
                                    window.open(`${getBaseUrl()}/offers?temporaryAccesstoken=${previewUrl}&type=preview`, '_blank');
                                }
                            }}
                            dangerouslySetInnerHTML={{
                                __html: emailPreview
                            }}
                            sx={{
                                '& pre': {
                                    fontFamily: 'Inter, Roboto, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
                                    fontSize: '14px',
                                    lineHeight: '1.6',
                                    color: '#0f172a',
                                    whiteSpace: 'pre-wrap',
                                    margin: 0,
                                    padding: 0,
                                    textAlign: 'left'
                                },
                                '& a': {
                                    display: 'inline',
                                    backgroundColor: 'transparent',
                                    color: '#3b82f6 !important',
                                    padding: '0',
                                    borderRadius: '0',
                                    textDecoration: 'underline !important',
                                    fontWeight: 400,
                                    fontSize: '12px',
                                    margin: '0',
                                    textAlign: 'left',
                                    width: 'auto',
                                    minWidth: 'auto',
                                    maxWidth: 'none',
                                    boxSizing: 'border-box',
                                    cursor: 'pointer',
                                    transition: 'color 0.2s',
                                    border: 'none',
                                    '&:hover': {
                                        color: '#2563eb !important',
                                        backgroundColor: 'transparent',
                                        textDecoration: 'underline !important'
                                    }
                                },
                                // Style body text (Hi, Best Regards) with softer color
                                '& pre > div:first-child': {
                                    color: '#334155 !important'
                                },
                                '& pre > div:last-child': {
                                    color: '#334155 !important'
                                },
                                // Style pricing lines - make service names bold and ensure proper spacing
                                '& pre > div:not(:first-child):not(:last-child)': {
                                    color: '#0f172a',
                                    marginBottom: '4px'
                                }
                            }}
                        />
                        {attachPDF && (
                            <Box sx={{
                                marginTop: '24px',
                                maxWidth: '280px',
                                backgroundColor: '#ffffff',
                                border: '1px solid #e2e8f0',
                                borderRadius: '8px',
                                padding: '12px 16px',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'space-between',
                                gap: '12px'
                            }}>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: '12px', flex: 1 }}>
                                    <FontAwesomeIcon icon={faFilePdf} style={{ fontSize: '24px', color: '#ef4444' }} />
                                    <Box>
                                        <Typography sx={{ fontSize: '13px', fontWeight: 500, color: '#0f172a', lineHeight: 1.2 }}>
                                            {quotePdfFilename(opportunity)}
                                        </Typography>
                                        <Typography sx={{ fontSize: '11px', color: '#64748b', lineHeight: 1.2, marginTop: '2px' }}>
                                            1.2 MB
                                        </Typography>
                                    </Box>
                                </Box>
                                <Tooltip title={pdfError || 'Download PDF'} arrow placement='top'>
                                    <span>
                                        <IconButton
                                            size='small'
                                            onClick={onDownloadPdf}
                                            disabled={downloadingPdf}
                                            sx={{
                                                color: '#3b82f6',
                                                '&:hover': {
                                                    backgroundColor: '#eff6ff'
                                                }
                                            }}
                                        >
                                            {downloadingPdf
                                                ? <CircularProgress size={16} />
                                                : <FontAwesomeIcon icon={faDownload} style={{ fontSize: '16px' }} />}
                                        </IconButton>
                                    </span>
                                </Tooltip>
                            </Box>
                        )}
                        {/* Copy button positioned absolutely in top right */}
                        <Box sx={{ 
                            position: 'absolute', 
                            top: '16px', 
                            right: '16px',
                            zIndex: 1
                        }}>
                            {copied === 'copied' ? (
                                <Typography variant='caption' sx={{ background: '#4d4ab6', color: 'white', padding: '6px 12px', borderRadius: '3px' }}>
                                    <FontAwesomeIcon icon={faCheck} />
                                    &nbsp;
                                    Copied!
                                </Typography>
                            ) : copied === 'copying' ? (
                                <Box sx={{ padding: '6px 12px' }}>
                                    <CircularProgress size={18}/>
                                </Box>
                            ) : (
                                <IconButton color='primary' onClick={copyToClip} sx={{ color: '#3b82f6' }}>
                                    <FontAwesomeIcon style={{ fontSize: '18px' }} icon={faCopy} />
                                </IconButton>
                            )}
                        </Box>
                    </Box>
                </Box>
            )}
            <QuotePdfNotice notice={pdfNotice} onClose={dismissPdfNotice} />
        </OpportunityPreviewAndSendBase>
    )
}

export default OpportunityPreviewAndSendOneOffEmail;